"use client";

import React from "react";
import { Button } from "./ui/button";
import { Card, CardContent } from "./ui/card";
import { SecuredFooter } from "./ui/secured-footer";

interface ErrorStateProps {
  /** Title to display (e.g., "Delete failed", "Adding contact failed") */
  title: string;
  /** Error message/description */
  message: string;
  /** Callback when "Try Again" is clicked */
  onTryAgain: () => void;
  /** Whether to wrap in Card component (default: true) */
  withCard?: boolean;
  /** Whether to show SecuredFooter (default: true) */
  withFooter?: boolean;
}

const ErrorState: React.FC<ErrorStateProps> = ({
  title,
  message,
  onTryAgain,
  withCard = true,
  withFooter = true,
}) => {
  const content = (
    <div className="flex flex-col items-center justify-center py-32 px-8">
      {/* Failed Icon - Circle with gradient border and gradient X */}
      <div className="relative w-[104px] h-[104px] mb-12">
        {/* Gradient border circle */}
        <div
          className="absolute inset-0 rounded-full"
          style={{
            background: "linear-gradient(135deg, #57249C 0%, #EB3366 100%)",
            padding: "2px",
          }}
        >
          <div className="w-full h-full rounded-full bg-white flex items-center justify-center">
            {/* Gradient X icon */}
            <svg
              width="40"
              height="40"
              viewBox="0 0 24 24"
              fill="none"
              stroke="url(#error-state-gradient)"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <defs>
                <linearGradient
                  id="error-state-gradient"
                  x1="0%"
                  y1="0%"
                  x2="100%"
                  y2="100%"
                >
                  <stop offset="0%" stopColor="#57249C" />
                  <stop offset="100%" stopColor="#EB3366" />
                </linearGradient>
              </defs>
              <line x1="18" y1="6" x2="6" y2="18" />
              <line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </div>
        </div>
      </div>

      {/* Title */}
      <h2 className="text-[32px] font-semibold text-[#33125F] mb-6 text-center leading-[40px]">
        {title}
      </h2>

      {/* Subtitle */}
      <p className="text-[20px] text-[#423E51] text-center mb-10 leading-[32px] max-w-[541px]">
        {message}
      </p>

      {/* Try Again Button */}
      <Button
        onClick={onTryAgain}
        className="h-[48px] w-[176px] rounded-full text-[14px] font-semibold text-white"
        style={{
          background:
            "linear-gradient(69.77deg, #57249C 10.66%, #EB3366 113.47%)",
        }}
      >
        Try Again
      </Button>
    </div>
  );

  if (!withCard) {
    return content;
  }

  return (
    <Card className="bg-white rounded-3xl border border-[#EBE9EF] overflow-visible">
      <CardContent className="p-0">
        {content}
        {withFooter && <SecuredFooter />}
      </CardContent>
    </Card>
  );
};

export default ErrorState;
